// Console log capture utility for debugging and ML training

export interface LogEntry {
  timestamp: number;
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  args: any[];
}

class LogCapture {
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000; // Limit to prevent memory issues
  private originalConsole: {
    log: typeof console.log;
    warn: typeof console.warn;
    error: typeof console.error;
    info: typeof console.info;
  };

  constructor() {
    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
    };
  }

  start() {
    // Intercept console.log
    console.log = (...args: any[]) => {
      this.addLog('log', args);
      this.originalConsole.log(...args);
    };

    // Intercept console.warn
    console.warn = (...args: any[]) => {
      this.addLog('warn', args);
      this.originalConsole.warn(...args);
    };

    // Intercept console.error
    console.error = (...args: any[]) => {
      this.addLog('error', args);
      this.originalConsole.error(...args);
    };

    // Intercept console.info
    console.info = (...args: any[]) => {
      this.addLog('info', args);
      this.originalConsole.info(...args);
    };

    console.log('[LogCapture] Started capturing console logs');
  }

  stop() {
    // Restore original console methods
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;

    this.originalConsole.log('[LogCapture] Stopped capturing console logs');
  }

  private addLog(level: LogEntry['level'], args: any[]) {
    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      message: args.map(arg => this.stringify(arg)).join(' '),
      args,
    };

    this.logs.push(entry);

    // Trim logs if exceeding max
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  private stringify(arg: any): string {
    if (typeof arg === 'string') {
      return arg;
    }
    if (arg instanceof Error) {
      return `${arg.name}: ${arg.message}\n${arg.stack}`;
    }
    try {
      return JSON.stringify(arg);
    } catch (e) {
      return String(arg);
    }
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  getLogsAsString(): string {
    return this.logs
      .map(entry => {
        const timestamp = new Date(entry.timestamp).toISOString();
        return `[${timestamp}] [${entry.level.toUpperCase()}] ${entry.message}`;
      })
      .join('\n');
  }

  clear() {
    this.logs = [];
    console.log('[LogCapture] Cleared all captured logs');
  }
}

// Singleton instance
export const logCapture = new LogCapture();

